import React, { createContext, useContext } from 'react';

import Tag from 'Components/Common/models/Tag';

export type EntityState = {
    name: string;
    email: string;
    phoneNumber: string;
    website: string;
    tags: Tag[];
    isSubgroup: boolean;
    parentId?: string | null;
    usesPoints: boolean;
    isIndependent: boolean;
    isEdit: boolean;
};

const initialState: EntityState = {
    name: '',
    email: '',
    phoneNumber: '',
    website: '',
    tags: [],
    isSubgroup: false,
    parentId: null,
    usesPoints: true,
    isIndependent: false,
    isEdit: false,
};

type Action =
    | { type: 'SET_ENTITY_STATE'; payload: EntityState }
    | { type: 'UPDATE_ENTITY_NAME'; payload: string }
    | { type: 'UPDATE_ENTITY_EMAIL'; payload: string }
    | { type: 'UPDATE_ENTITY_PHONE_NUMBER'; payload: string }
    | { type: 'UPDATE_ENTITY_WEBSITE'; payload: string }
    | { type: 'ADD_ENTITY_TAGS'; payload: string }
    | { type: 'REMOVE_ENTITY_TAGS'; payload: string }
    | { type: 'SET_IS_SUBGROUP'; payload: boolean }
    | { type: 'SET_PARENT_ID'; payload: string | null }
    | { type: 'SET_USES_POINTS'; payload: boolean }
    | { type: 'SET_IS_INDEPENDENT'; payload: boolean }

export const EntityContext = createContext<{
    state: EntityState;
    dispatch: React.Dispatch<Action>;
}>({
    state: initialState,
    dispatch: () => null,
});

export const entityReducer = (state: EntityState, action: Action): EntityState => {
    switch (action.type) {
    case 'SET_ENTITY_STATE':
        return { ...action.payload };
    case 'UPDATE_ENTITY_NAME':
        return { ...state, name: action.payload };
    case 'UPDATE_ENTITY_EMAIL':
        return { ...state, email: action.payload };
    case 'UPDATE_ENTITY_PHONE_NUMBER':
        return { ...state, phoneNumber: action.payload };
    case 'UPDATE_ENTITY_WEBSITE':
        return { ...state, website: action.payload };
    case 'ADD_ENTITY_TAGS': {
        const newTags = [...state.tags];
        newTags.push({ name: action.payload, isRequired: false });
        return { ...state, tags: newTags };
    }
    case 'REMOVE_ENTITY_TAGS': {
        const newTags = state.tags.filter((value) => value.name !== action.payload);
        return { ...state, tags: newTags };
    }
    case 'SET_IS_SUBGROUP':
        return { ...state, isSubgroup: action.payload, parentId: action.payload ? state.parentId : null };
    case 'SET_PARENT_ID':
        return { ...state, parentId: action.payload };
    case 'SET_USES_POINTS':
        return { ...state, usesPoints: action.payload };
    case 'SET_IS_INDEPENDENT':
        return {
            ...state,
            isIndependent: action.payload,
            isSubgroup: action.payload ? false : state.isSubgroup,
            parentId: action.payload ? null : state.parentId,
        };
    default:
        return state;
    }
};

export const useEntityContext = () => useContext(EntityContext);
